import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart3, TrendingUp, Users, Clock, AlertTriangle, RefreshCw,
  Target, DollarSign, Calendar, CheckCircle, XCircle, Briefcase,
  FileText, ListTodo, Filter, ChevronDown, ChevronUp, User, 
  Award, ArrowUpRight, ThumbsUp, ThumbsDown, Star
} from 'lucide-react';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const ReporteTareas = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('estado-global');
  
  // Estado Global de Tareas
  const [tareasGlobales, setTareasGlobales] = useState([]);
  const [conteoTareas, setConteoTareas] = useState({});
  const [loadingTareas, setLoadingTareas] = useState(false);
  
  // Gestión Líderes
  const [gestionLideres, setGestionLideres] = useState(null);
  const [loadingGestion, setLoadingGestion] = useState(false);
  
  const [fechaInicio, setFechaInicio] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [fechaFin, setFechaFin] = useState(() => new Date().toISOString().split('T')[0]);
  const [expandedCategories, setExpandedCategories] = useState({
    por_asignar: true,
    vencida: true,
    pendiente: true,
    realizada: false
  });

  useEffect(() => {
    if (activeTab === 'estado-global') {
      fetchEstadoGlobal();
    } else if (activeTab === 'gestion-lideres') {
      fetchGestionLideres();
    } else {
      fetchKPIs();
    }
  }, [activeTab]);

  const fetchKPIs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/kpis/dashboard-socios');
      setKpis(response.data);
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchEstadoGlobal = async () => {
    setLoadingTareas(true);
    try {
      const response = await api.get(`/kpis/estado-global-tareas?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
      setTareasGlobales(response.data.tareas);
      setConteoTareas(response.data.conteo);
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setLoadingTareas(false);
    }
  };

  const fetchGestionLideres = async () => {
    setLoadingGestion(true);
    try {
      const response = await api.get(`/kpis/gestion-lideres?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
      setGestionLideres(response.data);
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setLoadingGestion(false);
    }
  };

  const handleFiltrarFechas = () => {
    if (activeTab === 'gestion-lideres') {
      fetchGestionLideres();
    } else {
      fetchEstadoGlobal();
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getCategoriaInfo = (categoria) => {
    switch(categoria) {
      case 'por_asignar':
        return { 
          label: 'Tareas por Asignar', 
          color: 'bg-blue-600', 
          bgLight: 'bg-blue-50',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-200',
          icon: User
        };
      case 'vencida':
        return { 
          label: 'Tareas Vencidas', 
          color: 'bg-red-600', 
          bgLight: 'bg-red-50',
          textColor: 'text-red-700',
          borderColor: 'border-red-200',
          icon: AlertTriangle
        };
      case 'pendiente':
        return { 
          label: 'Tareas Pendientes', 
          color: 'bg-amber-500', 
          bgLight: 'bg-amber-50',
          textColor: 'text-amber-700',
          borderColor: 'border-amber-200',
          icon: Clock
        };
      case 'realizada':
        return { 
          label: 'Tareas Realizadas', 
          color: 'bg-green-600', 
          bgLight: 'bg-green-50',
          textColor: 'text-green-700',
          borderColor: 'border-green-200',
          icon: CheckCircle
        };
      default:
        return { 
          label: 'Tareas', 
          color: 'bg-slate-600', 
          bgLight: 'bg-slate-50',
          textColor: 'text-slate-700',
          borderColor: 'border-slate-200',
          icon: ListTodo
        };
    }
  };

  const getStatusBadge = (status, isVencida) => {
    if (status === 'Pendiente de Asignar') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Por Asignar</span>;
    }
    if (isVencida) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">Vencida</span>;
    }
    if (status === 'Aprobada') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Aprobada</span>;
    }
    if (status === 'Devuelta') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">Devuelta</span>;
    }
    if (status === 'En Revisión') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">En Revisión</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">{status}</span>;
  };

  // Agrupar tareas por categoría
  const tareasPorCategoria = {
    por_asignar: tareasGlobales.filter(t => t.categoria === 'por_asignar'),
    vencida: tareasGlobales.filter(t => t.categoria === 'vencida'),
    pendiente: tareasGlobales.filter(t => t.categoria === 'pendiente'),
    realizada: tareasGlobales.filter(t => t.categoria === 'realizada')
  };

  const KPICard = ({ title, value, subtitle, icon: Icon, color = 'primary', trend }) => (
    <Card className={`p-6 border-${color}-200 bg-${color === 'primary' ? 'white' : `${color}-50`}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-600">{title}</p>
          <p className={`text-3xl font-bold mt-2 text-${color === 'primary' ? 'slate-900' : `${color}-700`}`}>
            {value}
          </p>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl bg-${color === 'primary' ? 'primary/10' : `${color}-100`}`}>
          <Icon className={`w-6 h-6 text-${color === 'primary' ? 'primary' : `${color}-600`}`} />
        </div>
      </div>
      {trend && (
        <div className={`mt-3 flex items-center text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
          <TrendingUp className={`w-4 h-4 mr-1 ${trend < 0 ? 'rotate-180' : ''}`} />
          {Math.abs(trend)}% vs semana anterior
        </div>
      )}
    </Card>
  );

  const ProgressBar = ({ value, max, color = 'primary' }) => (
    <div className="w-full bg-slate-200 rounded-full h-2">
      <div 
        className={`h-2 rounded-full bg-${color}`}
        style={{ width: `${Math.min((value / max) * 100, 100)}%` }}
      />
    </div>
  );

  const renderCategoriaSection = (categoria) => {
    const info = getCategoriaInfo(categoria);
    const tareas = tareasPorCategoria[categoria] || [];
    const isExpanded = expandedCategories[categoria];
    const IconComponent = info.icon;

    return (
      <Card key={categoria} className={`mb-4 border ${info.borderColor} overflow-hidden`}>
        <button
          onClick={() => toggleCategory(categoria)}
          className={`w-full px-4 py-3 flex items-center justify-between ${info.bgLight} hover:opacity-90 transition-opacity`}
          data-testid={`toggle-${categoria}`}
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${info.color}`}>
              <IconComponent className="w-5 h-5 text-white" />
            </div>
            <span className={`font-semibold ${info.textColor}`}>{info.label}</span>
            <span className={`px-2 py-1 rounded-full text-sm font-medium ${info.color} text-white`}>
              {tareas.length}
            </span>
          </div>
          {isExpanded ? (
            <ChevronUp className={`w-5 h-5 ${info.textColor}`} />
          ) : (
            <ChevronDown className={`w-5 h-5 ${info.textColor}`} />
          )}
        </button>

        {isExpanded && (
          <div className="p-4">
            {tareas.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No hay tareas en esta categoría</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1A2E50]">
                      <th className="text-left py-3 px-4 font-semibold text-white">Usuario Asignado</th>
                      <th className="text-left py-3 px-4 font-semibold text-white">Cliente</th>
                      <th className="text-left py-3 px-4 font-semibold text-white">PSP / Minuta</th>
                      <th className="text-left py-3 px-4 font-semibold text-white">Tarea</th>
                      <th className="text-left py-3 px-4 font-semibold text-white">Área</th>
                      <th className="text-left py-3 px-4 font-semibold text-white">Vencimiento</th>
                      <th className="text-left py-3 px-4 font-semibold text-white">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tareas.map((tarea) => (
                      <tr 
                        key={tarea.id} 
                        className={`border-b border-slate-100 hover:bg-slate-50 ${
                          tarea.is_vencida ? 'bg-red-50/50' : ''
                        }`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${
                              tarea.responsible === 'Por asignar' || tarea.responsible === 'Sin asignar' 
                                ? 'bg-slate-400' 
                                : 'bg-primary'
                            }`}>
                              {tarea.responsible === 'Por asignar' || tarea.responsible === 'Sin asignar' 
                                ? '?' 
                                : tarea.responsible?.charAt(0) || '?'
                              }
                            </div>
                            <span className={`font-medium ${
                              tarea.responsible === 'Por asignar' || tarea.responsible === 'Sin asignar' 
                                ? 'text-slate-400 italic' 
                                : 'text-slate-800'
                            }`}>
                              {tarea.responsible || 'Sin asignar'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <span className="font-medium text-slate-800">{tarea.client_name}</span>
                        </td>
                        <td className="py-3 px-3">
                          {tarea.psp_folio ? (
                            <span className="text-primary font-medium">PSP {tarea.psp_folio}</span>
                          ) : tarea.meeting_id ? (
                            <span className="text-blue-600">Minuta</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3 max-w-xs">
                          <span className="text-slate-700 line-clamp-2" title={tarea.title}>
                            {tarea.title}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-700">
                            {tarea.area}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className={`font-medium ${
                            tarea.is_vencida 
                              ? 'text-red-600' 
                              : tarea.dias_para_vencer !== null && tarea.dias_para_vencer <= 3 
                                ? 'text-amber-600' 
                                : 'text-slate-700'
                          }`}>
                            {tarea.deadline_formatted}
                            {tarea.dias_para_vencer !== null && tarea.dias_para_vencer >= 0 && (
                              <span className="text-xs text-slate-500 ml-1">
                                ({tarea.dias_para_vencer}d)
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          {getStatusBadge(tarea.status, tarea.is_vencida)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-10 h-10 text-primary" strokeWidth={1.5} />
              <h1 className="text-4xl font-bold text-slate-900" data-testid="reporte-tareas-title">
                Reporte de Tareas
              </h1>
            </div>
            <p className="text-lg text-slate-600">
              Dashboard de KPIs y métricas de rendimiento exclusivo para Socios
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-6 bg-white border border-slate-200">
              <TabsTrigger value="estado-global" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
                <ListTodo className="w-4 h-4" />
                Estado Global de Tareas
              </TabsTrigger>
              <TabsTrigger value="gestion-lideres" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-gestion-lideres">
                <Users className="w-4 h-4" />
                Gestión Líderes
              </TabsTrigger>
              <TabsTrigger value="kpis" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
                <BarChart3 className="w-4 h-4" />
                KPIs y Métricas
              </TabsTrigger>
            </TabsList>

            {/* Tab: Estado Global de Tareas */}
            <TabsContent value="estado-global">
              {/* Filtros de Fecha */}
              <Card className="p-4 mb-6 border-slate-200">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-slate-500" />
                    <span className="font-medium text-slate-700">Filtrar por Rango de Fechas:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-600">Desde:</Label>
                    <Input
                      type="date"
                      value={fechaInicio}
                      onChange={(e) => setFechaInicio(e.target.value)}
                      className="w-40"
                      data-testid="fecha-inicio-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-600">Hasta:</Label>
                    <Input
                      type="date"
                      value={fechaFin}
                      onChange={(e) => setFechaFin(e.target.value)}
                      className="w-40"
                      data-testid="fecha-fin-input"
                    />
                  </div>
                  <Button 
                    onClick={handleFiltrarFechas}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="aplicar-filtro-btn"
                  >
                    Aplicar Filtro
                  </Button>
                </div>
              </Card>

              {/* Resumen de Conteo */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <Card className="p-4 border-blue-200 bg-blue-50">
                  <p className="text-sm text-blue-600 font-medium">Por Asignar</p>
                  <p className="text-2xl font-bold text-blue-700">{conteoTareas.por_asignar || 0}</p>
                </Card>
                <Card className="p-4 border-red-200 bg-red-50">
                  <p className="text-sm text-red-600 font-medium">Vencidas</p>
                  <p className="text-2xl font-bold text-red-700">{conteoTareas.vencidas || 0}</p>
                </Card>
                <Card className="p-4 border-amber-200 bg-amber-50">
                  <p className="text-sm text-amber-600 font-medium">Pendientes</p>
                  <p className="text-2xl font-bold text-amber-700">{conteoTareas.pendientes || 0}</p>
                </Card>
                <Card className="p-4 border-green-200 bg-green-50">
                  <p className="text-sm text-green-600 font-medium">Realizadas</p>
                  <p className="text-2xl font-bold text-green-700">{conteoTareas.realizadas || 0}</p>
                </Card>
                <Card className="p-4 border-slate-200 bg-white">
                  <p className="text-sm text-slate-600 font-medium">Total</p>
                  <p className="text-2xl font-bold text-slate-900">{conteoTareas.total || 0}</p>
                </Card>
              </div>

              {loadingTareas ? (
                <div className="flex items-center justify-center p-12">
                  <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : (
                <div>
                  {/* Secciones por Categoría */}
                  {renderCategoriaSection('por_asignar')}
                  {renderCategoriaSection('vencida')}
                  {renderCategoriaSection('pendiente')}
                  {renderCategoriaSection('realizada')}
                </div>
              )}
            </TabsContent>

            {/* Tab: KPIs y Métricas */}
            <TabsContent value="kpis">
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : kpis && (
                <>
                  {/* KPIs Principales */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <KPICard
                      title="Cumplimiento On-Time"
                      value={`${kpis.cumplimiento_on_time}%`}
                      subtitle="Tareas entregadas a tiempo"
                      icon={Target}
                      color={kpis.cumplimiento_on_time >= 80 ? 'green' : kpis.cumplimiento_on_time >= 60 ? 'amber' : 'red'}
                    />
                    <KPICard
                      title="Tiempo Promedio Cierre"
                      value={`${kpis.tiempo_promedio_cierre_dias} días`}
                      subtitle="Desde creación hasta aprobación"
                      icon={Clock}
                    />
                    <KPICard
                      title="Backlog Vencido"
                      value={kpis.backlog_vencido}
                      subtitle="Tareas pasadas de fecha"
                      icon={AlertTriangle}
                      color={kpis.backlog_vencido > 10 ? 'red' : kpis.backlog_vencido > 5 ? 'amber' : 'green'}
                    />
                    <KPICard
                      title="Nivel de Retrabajo"
                      value={`${kpis.nivel_retrabajo}%`}
                      subtitle="Tareas devueltas para corrección"
                      icon={RefreshCw}
                      color={kpis.nivel_retrabajo > 20 ? 'red' : kpis.nivel_retrabajo > 10 ? 'amber' : 'green'}
                    />
                  </div>

                  {/* Resumen General */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <Card className="p-4 border-slate-200 bg-primary/5">
                      <p className="text-sm text-slate-600">Total Tareas</p>
                      <p className="text-2xl font-bold text-primary">{kpis.resumen.total_tareas}</p>
                    </Card>
                    <Card className="p-4 border-slate-200 bg-amber-50">
                      <p className="text-sm text-slate-600">Pendientes</p>
                      <p className="text-2xl font-bold text-amber-700">{kpis.resumen.tareas_pendientes}</p>
                    </Card>
                    <Card className="p-4 border-slate-200 bg-green-50">
                      <p className="text-sm text-slate-600">Aprobadas</p>
                      <p className="text-2xl font-bold text-green-700">{kpis.resumen.tareas_aprobadas}</p>
                    </Card>
                    <Card className="p-4 border-slate-200 bg-blue-50">
                      <p className="text-sm text-slate-600">PSP Activas</p>
                      <p className="text-2xl font-bold text-blue-700">{kpis.resumen.propuestas_activas}</p>
                    </Card>
                  </div>

                  {/* Métricas por Área y Carga de Trabajo */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Métricas por Área */}
                    <Card className="p-6 border-slate-200">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-primary" />
                        Métricas por Área
                      </h3>
                      <div className="space-y-4">
                        {Object.entries(kpis.metricas_por_area).map(([area, data]) => (
                          <div key={area} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-slate-700">{area}</span>
                              <div className="flex items-center gap-4 text-sm">
                                <span className="text-amber-600">{data.pendientes} pend.</span>
                                <span className="text-green-600">{data.completadas} comp.</span>
                                {data.vencidas > 0 && (
                                  <span className="text-red-600">{data.vencidas} venc.</span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <div 
                                className="h-2 rounded-l bg-green-500"
                                style={{ width: `${(data.completadas / (data.total || 1)) * 100}%` }}
                              />
                              <div 
                                className="h-2 bg-amber-500"
                                style={{ width: `${(data.pendientes / (data.total || 1)) * 100}%` }}
                              />
                              <div 
                                className="h-2 rounded-r bg-red-500"
                                style={{ width: `${(data.vencidas / (data.total || 1)) * 100}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>

                    {/* Carga de Trabajo */}
                    <Card className="p-6 border-slate-200">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary" />
                        Carga de Trabajo por Profesional
                      </h3>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {Object.entries(kpis.carga_por_profesional).length > 0 ? (
                          Object.entries(kpis.carga_por_profesional).map(([nombre, tareas]) => (
                            <div key={nombre} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <span className="text-sm text-slate-700">{nombre}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24">
                                  <ProgressBar value={tareas} max={10} color="primary" />
                                </div>
                                <span className={`text-sm font-medium ${tareas > 8 ? 'text-red-600' : tareas > 5 ? 'text-amber-600' : 'text-green-600'}`}>
                                  {tareas} tareas
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-slate-500">No hay tareas activas asignadas</p>
                        )}
                      </div>
                      
                      {/* Capacidad Ociosa */}
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Capacidad Ociosa</span>
                          <span className={`text-sm font-medium ${kpis.capacidad_ociosa > 5 ? 'text-amber-600' : 'text-green-600'}`}>
                            {kpis.capacidad_ociosa} de {kpis.total_usuarios_activos} sin tareas
                          </span>
                        </div>
                      </div>
                    </Card>
                  </div>

                  {/* Margen por Cliente */}
                  <Card className="p-6 border-slate-200 mb-8">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-primary" />
                      Margen por Cliente (PSP Activas)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(kpis.margen_por_cliente).length > 0 ? (
                        Object.entries(kpis.margen_por_cliente).map(([cliente, monto]) => (
                          <div key={cliente} className="p-4 bg-slate-50 rounded-lg">
                            <p className="font-medium text-slate-800 truncate" title={cliente}>{cliente}</p>
                            <p className="text-xl font-bold text-green-600 mt-1">
                              ${new Intl.NumberFormat('es-CL').format(monto)}
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-slate-500 col-span-3">No hay propuestas activas con valor</p>
                      )}
                    </div>
                  </Card>

                  {/* Indicadores de Estado */}
                  <Card className="p-6 border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Indicadores de Salud del Sistema</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className={`inline-flex p-4 rounded-full ${kpis.cumplimiento_on_time >= 70 ? 'bg-green-100' : 'bg-red-100'}`}>
                          {kpis.cumplimiento_on_time >= 70 ? (
                            <CheckCircle className="w-8 h-8 text-green-600" />
                          ) : (
                            <XCircle className="w-8 h-8 text-red-600" />
                          )}
                        </div>
                        <p className="mt-2 font-medium text-slate-800">Cumplimiento</p>
                        <p className="text-sm text-slate-500">
                          {kpis.cumplimiento_on_time >= 70 ? 'Saludable' : 'Requiere atención'}
                        </p>
                      </div>
                      <div className="text-center">
                        <div className={`inline-flex p-4 rounded-full ${kpis.backlog_vencido <= 5 ? 'bg-green-100' : 'bg-amber-100'}`}>
                          {kpis.backlog_vencido <= 5 ? (
                            <CheckCircle className="w-8 h-8 text-green-600" />
                          ) : (
                            <AlertTriangle className="w-8 h-8 text-amber-600" />
                          )}
                        </div>
                        <p className="mt-2 font-medium text-slate-800">Backlog</p>
                        <p className="text-sm text-slate-500">
                          {kpis.backlog_vencido <= 5 ? 'Controlado' : 'Revisar prioridades'}
                        </p>
                      </div>
                      <div className="text-center">
                        <div className={`inline-flex p-4 rounded-full ${kpis.nivel_retrabajo <= 15 ? 'bg-green-100' : 'bg-red-100'}`}>
                          {kpis.nivel_retrabajo <= 15 ? (
                            <CheckCircle className="w-8 h-8 text-green-600" />
                          ) : (
                            <RefreshCw className="w-8 h-8 text-red-600" />
                          )}
                        </div>
                        <p className="mt-2 font-medium text-slate-800">Calidad</p>
                        <p className="text-sm text-slate-500">
                          {kpis.nivel_retrabajo <= 15 ? 'Alta calidad' : 'Alto retrabajo'}
                        </p>
                      </div>
                    </div>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* Tab: Gestión Líderes */}
            <TabsContent value="gestion-lideres">
              {/* Filtros de Fecha */}
              <Card className="p-4 mb-6 border-slate-200">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-slate-500" />
                    <span className="font-medium text-slate-700">Filtrar por Rango de Fechas:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-600">Desde:</Label>
                    <Input
                      type="date"
                      value={fechaInicio}
                      onChange={(e) => setFechaInicio(e.target.value)}
                      className="w-40"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-600">Hasta:</Label>
                    <Input
                      type="date"
                      value={fechaFin}
                      onChange={(e) => setFechaFin(e.target.value)}
                      className="w-40"
                    />
                  </div>
                  <Button 
                    onClick={handleFiltrarFechas}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="aplicar-filtro-gestion-btn"
                  >
                    Aplicar Filtro
                  </Button>
                </div>
              </Card>

              {loadingGestion ? (
                <div className="flex items-center justify-center p-12">
                  <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : gestionLideres && (
                <>
                  {/* KPIs Globales de Revisión */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
                    <Card className="p-4 border-green-200 bg-green-50">
                      <div className="flex items-center gap-2 mb-1">
                        <ThumbsUp className="w-4 h-4 text-green-600" />
                        <p className="text-sm text-green-600 font-medium">Aprobadas</p>
                      </div>
                      <p className="text-2xl font-bold text-green-700">{gestionLideres.kpis_globales.total_aprobadas}</p>
                    </Card>
                    <Card className="p-4 border-red-200 bg-red-50">
                      <div className="flex items-center gap-2 mb-1">
                        <ThumbsDown className="w-4 h-4 text-red-600" />
                        <p className="text-sm text-red-600 font-medium">Devueltas</p>
                      </div>
                      <p className="text-2xl font-bold text-red-700">{gestionLideres.kpis_globales.total_devueltas}</p>
                    </Card>
                    <Card className="p-4 border-primary/20 bg-primary/5">
                      <div className="flex items-center gap-2 mb-1">
                        <Target className="w-4 h-4 text-primary" />
                        <p className="text-sm text-primary font-medium">Tasa Aprob.</p>
                      </div>
                      <p className="text-2xl font-bold text-primary">{gestionLideres.kpis_globales.tasa_aprobacion_global}%</p>
                    </Card>
                    <Card className="p-4 border-blue-200 bg-blue-50">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <p className="text-sm text-blue-600 font-medium">Tiempo Prom.</p>
                      </div>
                      <p className="text-2xl font-bold text-blue-700">{gestionLideres.kpis_globales.tiempo_promedio_revision_dias}d</p>
                    </Card>
                    <Card className="p-4 border-amber-200 bg-amber-50">
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowUpRight className="w-4 h-4 text-amber-600" />
                        <p className="text-sm text-amber-600 font-medium">Escaladas</p>
                      </div>
                      <p className="text-2xl font-bold text-amber-700">{gestionLideres.kpis_globales.total_escaladas_socios}</p>
                    </Card>
                    <Card className="p-4 border-purple-200 bg-purple-50">
                      <div className="flex items-center gap-2 mb-1">
                        <Star className="w-4 h-4 text-purple-600" />
                        <p className="text-sm text-purple-600 font-medium">En Rev. Socios</p>
                      </div>
                      <p className="text-2xl font-bold text-purple-700">{gestionLideres.kpis_globales.tareas_en_revision_socios}</p>
                    </Card>
                    <Card className="p-4 border-orange-200 bg-orange-50">
                      <div className="flex items-center gap-2 mb-1">
                        <RefreshCw className="w-4 h-4 text-orange-600" />
                        <p className="text-sm text-orange-600 font-medium">Dev. Socios</p>
                      </div>
                      <p className="text-2xl font-bold text-orange-700">{gestionLideres.kpis_globales.tareas_devueltas_por_socios}</p>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                    {/* Ranking de Tasa de Aprobación */}
                    <Card className="p-6 border-slate-200">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Award className="w-5 h-5 text-amber-500" />
                        Top Líderes por Aprobación
                      </h3>
                      {gestionLideres.ranking_tasa_aprobacion.length > 0 ? (
                        <div className="space-y-3">
                          {gestionLideres.ranking_tasa_aprobacion.map((lider, index) => (
                            <div key={lider.nombre} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                                  index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : 'bg-slate-300'
                                }`}>
                                  {index + 1}
                                </div>
                                <span className="font-medium text-slate-800">{lider.nombre}</span>
                              </div>
                              <div className="text-right">
                                <p className={`font-bold ${lider.tasa >= 80 ? 'text-green-600' : lider.tasa >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                                  {lider.tasa}%
                                </p>
                                <p className="text-xs text-slate-500">{lider.total} revisiones</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 text-center py-4">No hay suficientes datos (mínimo 5 revisiones)</p>
                      )}
                    </Card>

                    {/* Detalle por Líder */}
                    <Card className="p-6 border-slate-200 lg:col-span-2">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary" />
                        Detalle por Líder
                      </h3>
                      {Object.keys(gestionLideres.metricas_por_lider).length > 0 ? (
                        <div className="overflow-x-auto rounded-lg border border-slate-200">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-[#1A2E50]">
                                <th className="text-left py-3 px-4 font-semibold text-white">Líder</th>
                                <th className="text-center py-3 px-4 font-semibold text-white">Áreas</th>
                                <th className="text-center py-3 px-4 font-semibold text-green-300">Aprobadas</th>
                                <th className="text-center py-3 px-4 font-semibold text-red-300">Devueltas</th>
                                <th className="text-center py-3 px-4 font-semibold text-amber-300">Escaladas</th>
                                <th className="text-center py-3 px-4 font-semibold text-white">Tasa</th>
                                <th className="text-center py-3 px-4 font-semibold text-white">T. Prom.</th>
                                <th className="text-center py-3 px-4 font-semibold text-blue-300">Pendientes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(gestionLideres.metricas_por_lider).map(([nombre, data]) => (
                                <tr key={nombre} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-2">
                                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-medium">
                                        {nombre.charAt(0)}
                                      </div>
                                      <span className="font-medium text-slate-800">{nombre}</span>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    <div className="flex flex-wrap gap-1 justify-center">
                                      {data.areas?.slice(0, 2).map(area => (
                                        <span key={area} className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">
                                          {area.substring(0, 4)}
                                        </span>
                                      ))}
                                      {data.areas?.length > 2 && (
                                        <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">+{data.areas.length - 2}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="py-3 px-3 text-center">
                                    <span className="font-semibold text-green-600">{data.aprobadas}</span>
                                  </td>
                                  <td className="py-3 px-3 text-center">
                                    <span className="font-semibold text-red-600">{data.devueltas}</span>
                                  </td>
                                  <td className="py-3 px-3 text-center">
                                    <span className="font-semibold text-amber-600">{data.escaladas_socios}</span>
                                  </td>
                                  <td className="py-3 px-3 text-center">
                                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                      data.tasa_aprobacion >= 80 ? 'bg-green-100 text-green-700' :
                                      data.tasa_aprobacion >= 60 ? 'bg-amber-100 text-amber-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {data.tasa_aprobacion}%
                                    </span>
                                  </td>
                                  <td className="py-3 px-3 text-center">
                                    <span className="text-slate-700">{data.tiempo_promedio_dias}d</span>
                                  </td>
                                  <td className="py-3 px-3 text-center">
                                    <span className={`font-semibold ${data.pendientes_revision > 5 ? 'text-red-600' : 'text-blue-600'}`}>
                                      {data.pendientes_revision}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 text-center py-8">No hay datos de gestión de líderes en el período seleccionado</p>
                      )}
                    </Card>
                  </div>

                  {/* Indicadores de Estado del Flujo de Revisión */}
                  <Card className="p-6 border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Estado del Flujo de Revisión</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      <div className="text-center">
                        <div className={`inline-flex p-4 rounded-full ${gestionLideres.kpis_globales.tasa_aprobacion_global >= 70 ? 'bg-green-100' : 'bg-red-100'}`}>
                          {gestionLideres.kpis_globales.tasa_aprobacion_global >= 70 ? (
                            <CheckCircle className="w-8 h-8 text-green-600" />
                          ) : (
                            <XCircle className="w-8 h-8 text-red-600" />
                          )}
                        </div>
                        <p className="mt-2 font-medium text-slate-800">Tasa de Aprobación</p>
                        <p className="text-sm text-slate-500">
                          {gestionLideres.kpis_globales.tasa_aprobacion_global >= 70 ? 'Saludable (≥70%)' : 'Requiere atención'}
                        </p>
                      </div>
                      <div className="text-center">
                        <div className={`inline-flex p-4 rounded-full ${gestionLideres.kpis_globales.tiempo_promedio_revision_dias <= 3 ? 'bg-green-100' : 'bg-amber-100'}`}>
                          {gestionLideres.kpis_globales.tiempo_promedio_revision_dias <= 3 ? (
                            <CheckCircle className="w-8 h-8 text-green-600" />
                          ) : (
                            <Clock className="w-8 h-8 text-amber-600" />
                          )}
                        </div>
                        <p className="mt-2 font-medium text-slate-800">Tiempo de Revisión</p>
                        <p className="text-sm text-slate-500">
                          {gestionLideres.kpis_globales.tiempo_promedio_revision_dias <= 3 ? 'Ágil (≤3 días)' : 'Revisar tiempos'}
                        </p>
                      </div>
                      <div className="text-center">
                        <div className={`inline-flex p-4 rounded-full ${gestionLideres.kpis_globales.tareas_en_revision_socios <= 5 ? 'bg-green-100' : 'bg-purple-100'}`}>
                          {gestionLideres.kpis_globales.tareas_en_revision_socios <= 5 ? (
                            <CheckCircle className="w-8 h-8 text-green-600" />
                          ) : (
                            <Star className="w-8 h-8 text-purple-600" />
                          )}
                        </div>
                        <p className="mt-2 font-medium text-slate-800">Cola de Socios</p>
                        <p className="text-sm text-slate-500">
                          {gestionLideres.kpis_globales.tareas_en_revision_socios <= 5 ? 'Controlada' : 'Alta carga'}
                        </p>
                      </div>
                      <div className="text-center">
                        <div className={`inline-flex p-4 rounded-full ${gestionLideres.kpis_globales.tareas_devueltas_por_socios === 0 ? 'bg-green-100' : 'bg-orange-100'}`}>
                          {gestionLideres.kpis_globales.tareas_devueltas_por_socios === 0 ? (
                            <CheckCircle className="w-8 h-8 text-green-600" />
                          ) : (
                            <RefreshCw className="w-8 h-8 text-orange-600" />
                          )}
                        </div>
                        <p className="mt-2 font-medium text-slate-800">Pendientes Líderes</p>
                        <p className="text-sm text-slate-500">
                          {gestionLideres.kpis_globales.tareas_devueltas_por_socios === 0 ? 'Sin pendientes' : `${gestionLideres.kpis_globales.tareas_devueltas_por_socios} por gestionar`}
                        </p>
                      </div>
                    </div>
                  </Card>
                </>
              )}
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};

export default ReporteTareas;
